<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { RouterLink, RouterView } from "vue-router";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import LanguageSwitcher from "@/components/LanguageSwitcher.vue";
import { useAuthStore } from "@/stores/auth";
import { features } from "@/config/features";

const { t } = useI18n();
const appName = import.meta.env.VITE_APP_NAME || t("app.name");
const appVersion = __APP_VERSION__;
const authStore = useAuthStore();
const { user } = storeToRefs(authStore);
const { logout } = authStore;

const isMenuOpen = ref(false);
const drawerRef = ref(null);

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};
const closeMenu = () => {
  isMenuOpen.value = false;
};
const handleLogout = () => {
  logout();
  closeMenu();
};

function handleClickOutside(e) {
  if (
    isMenuOpen.value &&
    drawerRef.value &&
    !drawerRef.value.contains(e.target)
  ) {
    closeMenu();
  }
}

function handleKeydown(e) {
  if (e.key === "Escape" && isMenuOpen.value) closeMenu();
}

onMounted(() => {
  document.addEventListener("mousedown", handleClickOutside);
  document.addEventListener("keydown", handleKeydown);
});
onUnmounted(() => {
  document.removeEventListener("mousedown", handleClickOutside);
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<template>
  <div class="app">
    <header class="header">
      <nav class="nav">
        <RouterLink to="/" class="logo">
          <img src="/logo.png" alt="" class="logo-img" aria-hidden="true" />
          <span class="logo-text">{{ appName }}</span>
        </RouterLink>

        <!-- Desktop: page links (center) -->
        <div class="nav-links">
          <RouterLink to="/" class="nav-link">{{ $t("nav.home") }}</RouterLink>
          <RouterLink to="/about" class="nav-link">{{
            $t("nav.about")
          }}</RouterLink>
          <!-- Hidden during the staged rollout — see src/config/features.ts -->
          <RouterLink v-if="features.classes" to="/classes" class="nav-link">{{
            $t("nav.classes")
          }}</RouterLink>
          <RouterLink v-if="features.passes" to="/passes" class="nav-link">{{
            $t("nav.passes")
          }}</RouterLink>
          <RouterLink
            v-if="features.bookings"
            to="/bookings"
            class="nav-link"
            >{{ $t("nav.bookings") }}</RouterLink
          >
          <RouterLink to="/video" class="nav-link">{{
            $t("nav.video")
          }}</RouterLink>
          <RouterLink v-if="features.profile" to="/profile" class="nav-link">{{
            $t("nav.profile")
          }}</RouterLink>
          <RouterLink
            v-if="features.admin && authStore.isAdmin"
            to="/admin"
            class="nav-link"
            >{{ $t("nav.admin") }}</RouterLink
          >
        </div>

        <!-- Desktop: user section (right) -->
        <div v-if="user" class="nav-user">
          <LanguageSwitcher />
          <button class="nav-link nav-logout" @click="logout">
            {{ $t("auth.logout") }}
          </button>
        </div>

        <!-- Mobile only: hamburger -->
        <button
          class="menu-toggle"
          @click="toggleMenu"
          :aria-expanded="isMenuOpen"
          :aria-label="isMenuOpen ? $t('nav.close') : $t('nav.menu')"
        >
          <span class="menu-icon" :class="{ open: isMenuOpen }"></span>
        </button>
      </nav>
    </header>

    <!-- Mobile drawer -->
    <div
      v-if="isMenuOpen"
      class="nav-drawer"
      role="dialog"
      :aria-label="$t('nav.menu')"
    >
      <div ref="drawerRef" class="nav-drawer-panel">
        <RouterLink to="/about" class="drawer-link" @click="closeMenu">{{
          $t("nav.about")
        }}</RouterLink>
        <!-- Must mirror the desktop links above — same feature flags. -->
        <RouterLink
          v-if="features.classes"
          to="/classes"
          class="drawer-link"
          @click="closeMenu"
          >{{ $t("nav.classes") }}</RouterLink
        >
        <RouterLink
          v-if="features.passes"
          to="/passes"
          class="drawer-link"
          @click="closeMenu"
          >{{ $t("nav.passes") }}</RouterLink
        >
        <RouterLink
          v-if="features.bookings"
          to="/bookings"
          class="drawer-link"
          @click="closeMenu"
          >{{ $t("nav.bookings") }}</RouterLink
        >
        <RouterLink to="/video" class="drawer-link" @click="closeMenu">{{
          $t("nav.video")
        }}</RouterLink>
        <RouterLink
          v-if="features.profile"
          to="/profile"
          class="drawer-link"
          @click="closeMenu"
          >{{ $t("nav.profile") }}</RouterLink
        >
        <RouterLink
          v-if="features.admin && authStore.isAdmin"
          to="/admin"
          class="drawer-link"
          @click="closeMenu"
          >{{ $t("nav.admin") }}</RouterLink
        >

        <template v-if="user">
          <div class="drawer-divider" />
          <div class="drawer-lang"><LanguageSwitcher /></div>
          <div class="drawer-divider" />
          <button class="drawer-link drawer-logout" @click="handleLogout">
            {{ $t("auth.logout") }}
          </button>
        </template>
      </div>
    </div>

    <main class="main">
      <RouterView />
    </main>

    <footer
      class="footer"
      :data-version="appVersion"
      :data-tenant="user?.tenantId ?? ''"
    >
      <p>&copy; {{ new Date().getFullYear() }} {{ appName }}</p>
    </footer>
  </div>
</template>

<style scoped>
.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.header {
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  padding: 0.75rem 1.5rem;
  position: relative;
  z-index: 201;
}

.nav {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: var(--color-text);
  font-weight: 600;
  font-size: 1.1rem;
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.logo-img {
  height: 28px;
  width: auto;
  display: block;
  object-fit: contain;
}

.nav-links {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
}

.nav-user {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}

.nav-link {
  color: var(--color-text);
  text-decoration: none;
  padding: 0.5rem 0.75rem;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  font-size: 0.9375rem;
  transition:
    color 0.2s,
    background-color 0.2s;
  background: none;
  border: none;
  cursor: pointer;
  white-space: nowrap;
}

.nav-link:hover {
  color: var(--color-primary);
  background: var(--color-warm);
}

.nav-link.router-link-active {
  color: var(--color-primary);
  font-weight: 500;
}

.nav-link:focus-visible {
  outline: 3px solid var(--color-primary);
  outline-offset: 2px;
}

.nav-logout {
  color: var(--color-text-muted);
}

.nav-logout:hover {
  color: var(--color-danger);
  background: color-mix(in srgb, var(--color-danger) 6%, white);
}

/* Hamburger: mobile only */
.menu-toggle {
  display: none;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: var(--color-text);
  margin-left: auto;
  flex-shrink: 0;
}

.menu-toggle:focus-visible {
  outline: 3px solid var(--color-primary);
  outline-offset: 2px;
  border-radius: 4px;
}

/* Hamburger icon — three bars → X */
.menu-icon,
.menu-icon::before,
.menu-icon::after {
  display: block;
  width: 22px;
  height: 2px;
  background: currentColor;
  transition: transform 0.2s;
}

.menu-icon {
  position: relative;
}

.menu-icon::before,
.menu-icon::after {
  content: "";
  position: absolute;
  left: 0;
}

.menu-icon::before {
  top: -7px;
}
.menu-icon::after {
  top: 7px;
}

.menu-icon.open {
  background: transparent;
}
.menu-icon.open::before {
  transform: rotate(45deg);
  top: 0;
}
.menu-icon.open::after {
  transform: rotate(-45deg);
  top: 0;
}

/* Mobile */
@media (max-width: 639px) {
  .nav-links,
  .nav-user {
    display: none;
  }

  .menu-toggle {
    display: flex;
  }
}

/* Drawer (mobile only) */
.nav-drawer {
  position: fixed;
  inset: 0;
  z-index: 200;
}

.nav-drawer-panel {
  position: absolute;
  top: 65px;
  left: 0;
  right: 0;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  box-shadow: var(--shadow-md);
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.drawer-link {
  color: var(--color-text);
  text-decoration: none;
  padding: 0.75rem 1rem;
  min-height: 44px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  font-size: 0.9375rem;
  transition:
    color 0.2s,
    background-color 0.2s;
  background: none;
  border: none;
  cursor: pointer;
  width: 100%;
  text-align: left;
}

.drawer-link:hover {
  background: var(--color-warm);
  color: var(--color-primary);
}

.drawer-link.router-link-active {
  color: var(--color-primary);
  font-weight: 500;
}

.drawer-link:focus-visible {
  outline: 3px solid var(--color-primary);
  outline-offset: -2px;
}

.drawer-divider {
  height: 1px;
  background: var(--color-border);
  margin: 0.25rem 0;
}

.drawer-lang {
  padding: 0.25rem 0.5rem;
}

.drawer-logout {
  color: var(--color-text-muted);
}

.drawer-logout:hover {
  color: var(--color-danger);
  background: color-mix(in srgb, var(--color-danger) 6%, white);
}

/**
 * A bare shell — no padding, no width cap.
 *
 * Page chrome used to sit here (2rem padding, max-width 1200px), which meant
 * full-bleed sections could never reach the screen edge and `.container`
 * (capped at 1280px) was silently clamped to 1200px. Spacing now belongs to
 * the sections themselves: a view either wraps its content in `.container`
 * or deliberately runs edge to edge.
 */
.main {
  flex: 1;
  width: 100%;
}

.footer {
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
  padding: 1.5rem 2rem;
  text-align: center;
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.footer p {
  max-width: 1200px;
  margin: 0 auto;
}

@media (max-width: 639px) {
  .footer {
    padding: 1rem;
  }
}
</style>
